package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.Milestone
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

class MilestoneRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Milestone>> =
        db.milestoneQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeByObjective(objectiveId: String): Flow<List<Milestone>> =
        db.milestoneQueries.selectByObjective(objectiveId).asFlow().mapToList(Dispatchers.Default)

    fun observeById(id: String): Flow<Milestone?> =
        db.milestoneQueries.selectById(id).asFlow().mapToOneOrNull(Dispatchers.Default)

    suspend fun create(
        objectiveId: String,
        title: String,
        description: String? = null,
        deadline: String? = null,
        progress: Long = 0,
        orderIndex: Long = 0,
        id: String = newId("ms"),
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.milestoneQueries.insert(id, objectiveId, title, description, deadline, progress, orderIndex, now, now)
        id
    }

    suspend fun update(
        id: String,
        title: String,
        description: String?,
        deadline: String?,
        progress: Long,
        orderIndex: Long,
    ) = withContext(Dispatchers.Default) {
        db.milestoneQueries.update(title, description, deadline, progress, orderIndex, nowMillis(), id)
    }

    suspend fun updateProgress(id: String, progress: Long) = withContext(Dispatchers.Default) {
        db.milestoneQueries.updateProgress(progress, nowMillis(), id)
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.milestoneQueries.softDelete(now, now, id)
    }

    /**
     * Réordonne les jalons d'un objectif (spec §7.5).
     * fromIndex / toIndex sont les positions dans la liste triée par order_index existante.
     * No-op si égaux ou hors plage.
     */
    suspend fun reorderInObjective(objectiveId: String, fromIndex: Int, toIndex: Int) =
        withContext(Dispatchers.Default) {
            if (fromIndex == toIndex) return@withContext
            val list = db.milestoneQueries.selectByObjective(objectiveId).executeAsList().toMutableList()
            if (fromIndex !in list.indices) return@withContext
            val clampedTo = toIndex.coerceIn(0, list.lastIndex)
            val item = list.removeAt(fromIndex)
            list.add(clampedTo, item)
            val now = nowMillis()
            list.forEachIndexed { i, m ->
                if (m.order_index != i.toLong()) {
                    db.milestoneQueries.updateOrder(i.toLong(), now, m.id)
                }
            }
        }
}